import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiUsers, FiActivity, FiAward, FiVolume2, FiTrendingUp, FiMail } from 'react-icons/fi';
import { GiCrossedSwords } from 'react-icons/gi';
import StoreButtons from '../components/StoreButtons';
import { getProduct } from '../data/catalog';
import Reel from '../components/Reel';

const FeatureCard = ({ icon, title, desc }) => (
  <div className="bg-card border border-border p-6 rounded-none hover:border-accent-red/50 transition-all group">
    <div className="w-12 h-12 rounded-md bg-accent-red/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
      <SafeIcon icon={icon} className="w-6 h-6 text-accent-red" />
    </div>
    <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
    <p className="text-muted text-sm leading-relaxed">{desc}</p>
  </div>
);

// The roster as it stands in the game (RosterBuilder), with each fighter's nickname and armatura.
const GLADIATORS = [
  { id: 'marcus', name: 'Marcus', nick: 'Thrax', origin: 'Thrace', arm: 'Thraex', bio: 'Taken in a border raid, sold in Capua. Quick feet, a curved blade and nothing left to lose.' },
  { id: 'vindex', name: 'Vindex', nick: 'Gallus', origin: 'Gaul', arm: 'Murmillo', bio: 'A Gallic warrior turned arena wall. He waits behind his scutum until you make the mistake.' },
  { id: 'nikias', name: 'Nikias', nick: 'Graecus', origin: 'Greece', arm: 'Hoplomachus', bio: "A hoplite's son who fights like his fathers: spear first, dagger when it breaks." },
  { id: 'hylas', name: 'Hylas', nick: 'Numida', origin: 'Numidia', arm: 'Retiarius', bio: 'No helmet, no shield: a net, a trident and the fastest legs in the ludus.' },
  { id: 'flavus', name: 'Flavus', nick: 'Germanus', origin: 'Germania', arm: 'Secutor', bio: "The netman's hunter. Half blind inside his smooth helmet, he never stops coming." },
  { id: 'brennus', name: 'Brennus', nick: 'Ferreus', origin: 'Gaul', arm: 'Provocator', bio: "A soldier's build under a soldier's breastplate. He trades blows and wins the trade." },
  { id: 'taras', name: 'Taras', nick: 'Dacus', origin: 'Dacia', arm: 'Dimachaerus', bio: 'Two swords and no shield. The crowd loves him; his opponents do not last.' },
  { id: 'lucius', name: 'Lucius', nick: 'Hispanus', origin: 'Hispania', arm: 'Eques', bio: 'A horseman from the Baetis valley who fights on foot: round shield, short sword, fast.' },
  { id: 'achillia', name: 'Achillia', nick: null, origin: 'Asia Minor', arm: 'Gladiatrix', bio: 'A stage name carved on a relief at Halicarnassus. Here she fights again.' },
  { id: 'amazon', name: 'Amazon', nick: null, origin: 'Asia Minor', arm: 'Gladiatrix', bio: "Achillia's opponent on the same relief. Patient, exact, deadly on the counter." },
  { id: 'paccius', name: 'Paccius', nick: 'Senex', origin: 'Samnium', arm: 'Samnis', bio: 'A Samnite of the old games, scarred and slow and very hard to kill.' },
  { id: 'catumandus', name: 'Catumandus', nick: 'Comatus', origin: 'Gaul', arm: 'Gallus', bio: 'Long sword, long hair, longer memory. He fights Rome one Roman at a time.' },
  { id: 'iarbas', name: 'Iarbas', nick: 'Maurus', origin: 'Mauretania', arm: 'Veles', bio: 'A desert runner who kills from a distance and is gone before you arrive.' },
  { id: 'glaucus', name: 'Glaucus', nick: 'Vulpes', origin: 'Ionia', arm: 'Laquearius', bio: "A herdsman's trick turned into a show. Get caught in his rope and you are on the sand." },
  { id: 'cotto', name: 'Cotto', nick: 'Incus', origin: 'Gaul', arm: 'Crupellarius', bio: 'Iron from head to foot. Steel will not stop him. Gravity might.' },
  { id: 'spartacus', name: 'Spartacus', nick: null, origin: 'Thrace', arm: 'Thraex', bio: 'The Thracian who broke out of Capua and shook Rome. A legend to unlock.' },
];

const Rudis = () => {
  const PRODUCT = getProduct('rudis');

  return (
    <div className="flex flex-col">
      {/* Hero */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        <img src={PRODUCT.image} alt="" aria-hidden="true" className="absolute inset-0 z-0 h-full w-full object-cover opacity-30" />
        <div className="absolute inset-0 z-0 bg-gradient-to-b from-background/40 via-background/30 to-background" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-accent-red/10 border border-accent-red/20 text-accent-red font-mono text-xs uppercase tracking-[0.16em] mb-6">
              <SafeIcon icon={GiCrossedSwords} /> Physics gladiator duels · 1-2 players
            </div>
            <h1 className="text-5xl md:text-8xl font-black text-white mb-4 tracking-tighter uppercase italic">
              Rudis
            </h1>
            <p className="text-2xl md:text-3xl font-bold text-accent-red uppercase tracking-widest mb-8">Blood for Freedom</p>
            <p className="text-xl text-muted max-w-3xl mx-auto mb-10 leading-relaxed font-light italic">
              "Ave! Morituri te salutant." Every blow is real. The crowd decides the rest.
            </p>
            <div className="flex flex-col items-center justify-center gap-5">
              <StoreButtons stores={PRODUCT.stores} accentColor="bg-ember hover:bg-ember-bright" className="justify-center" />
              <a href="https://newsletter.mediaverselabs.com/subscribe"
                 className="inline-flex items-center gap-2 text-sm font-medium text-muted hover:text-white transition-colors">
                <SafeIcon icon={FiMail} /> Get the launch news first
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pitch */}
      <section className="py-12 border-y border-border bg-card/20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-lg text-primary leading-relaxed">
            Capua, the first century BC. You are a slave with a sword, and the only way out is through the arena.
            RUDIS is a one-on-one gladiator duel where nothing is a canned animation: your guard, your footwork and the
            weight of your kit decide where the steel lands. Draw first blood, win the stands, and when a man goes down,
            the crowd and the editor decide: missio or iugula. Earn enough and you will hold the rudis, the wooden sword
            of a free man.
          </p>
        </div>
      </section>

      {/* Reel */}
      <section className="py-16">
        <div className="mx-auto max-w-6xl px-4">
          <p className="rule-label mb-6">Reel · RUDIS</p>
          <Reel frames={PRODUCT.frames} label="RUDIS" sound={false} ratio="aspect-video" />
          <p className="text-xs text-muted mt-3">In-engine captures from the current development build.</p>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-white mb-4 uppercase tracking-tight">Game Features</h2>
            <p className="text-muted">The games of Rome, fought blow by blow.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <FeatureCard icon={FiActivity} title="Every Blow Simulated" desc="Bodies, blades and shields run in a 120 Hz physics simulation. A cut lands where the edge meets flesh, a shield stops what it covers, and a heavy man moves like one." />
            <FeatureCard icon={GiCrossedSwords} title="16 Gladiators" desc="Thraex, murmillo, retiarius, secutor, hoplomachus, provocator, dimachaerus and more, each with his own kit, reach and way of fighting." />
            <FeatureCard icon={FiUsers} title="The Crowd Decides" desc="Fight well and the stands take up your name. Fight dull and they boo, and the lorarii come out with the whips. When a man is beaten, they call for missio or iugula." />
            <FeatureCard icon={FiVolume2} title="The Full Spectacle" desc="The herald presents each fighter, the pair salute the editor's box, and the referee calls the bout. Win, and the victor takes his lap of the sand." />
            <FeatureCard icon={FiTrendingUp} title="Fight for Freedom" desc="Train at the palus in your lanista's ludus, climb from the provincial games to the great arenas, and earn the rudis." />
            <FeatureCard icon={FiAward} title="Local Versus" desc="Two players, one screen, keyboard and gamepad. Settle it on the sand." />
          </div>
        </div>
      </section>

      {/* Roster */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-black text-white uppercase italic mb-3">The <span className="text-accent-red">Familia</span></h2>
          <p className="text-muted mb-10 max-w-3xl">
            Sixteen fighters from across the Roman world. Names, kits and stories are dramatized from what the sources tell us about the games.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {GLADIATORS.map((g) => (
              <div key={g.id} className="bg-card border border-border hover:border-accent-red/50 transition-colors overflow-hidden">
                <img src={`/products/rudis/${g.id}.webp`} alt={`${g.name}, ${g.arm}`} loading="lazy"
                     className="w-full aspect-[4/5] object-cover object-top" />
                <div className="p-4">
                  <h4 className="text-white font-bold leading-tight">
                    {g.name}{g.nick && <span className="text-muted font-normal italic"> "{g.nick}"</span>}
                  </h4>
                  <p className="font-mono text-[11px] uppercase tracking-wider text-accent-red mt-1">{g.arm} · {g.origin}</p>
                  <p className="text-xs text-muted mt-2 leading-relaxed">{g.bio}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Details */}
      <section className="pb-24">
        <div className="max-w-3xl mx-auto px-4">
          <div className="bg-card border border-border rounded-none p-8">
            <h3 className="text-xl font-bold text-white mb-6 uppercase">Details</h3>
            <div className="space-y-4 text-sm">
              {[
                ['Platform', 'Windows PC'],
                ['Stores', 'Steam and Epic Games Store (coming soon)'],
                ['Players', '1 player, 2 player local versus'],
                ['Controls', 'Keyboard and mouse, gamepad'],
                ['Languages', 'English, Spanish'],
                ['Status', 'In development'],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 py-2 border-b border-border/50">
                  <span className="text-muted">{k}</span>
                  <span className="text-white text-right">{v}</span>
                </div>
              ))}
            </div>
            <div className="mt-8">
              <StoreButtons stores={PRODUCT.stores} accentColor="bg-ember hover:bg-ember-bright" />
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 relative overflow-hidden text-center">
        <div className="absolute inset-0 bg-accent-red/5 -z-10" />
        <div className="max-w-3xl mx-auto px-4">
          <SafeIcon icon={GiCrossedSwords} className="w-16 h-16 text-accent-red mx-auto mb-8" />
          <h2 className="text-5xl font-black text-white mb-6 uppercase italic">Be in the stands on day one</h2>
          <p className="text-muted mb-10 text-lg">
            RUDIS is coming to Steam and the Epic Games Store. Join the MediaVerse Labs newsletter and we will tell you the moment the gates open.
          </p>
          <a href="https://newsletter.mediaverselabs.com/subscribe"
             className="btn-primary bg-ember hover:bg-ember-bright text-white px-10 py-4 inline-flex decoration-transparent">
            Get the launch news
          </a>
        </div>
      </section>
    </div>
  );
};

export default Rudis;
